import React, { useEffect, useRef, useState } from "react";
import { useSocketChannel } from "src/_common/hooks/custom/useSocketChannel";
import { SOCKET_CHANNEL } from "src/_config";
import { Modal } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { toPng } from "html-to-image";
import { useEventApi } from "src/_common/hooks/actions/event/appEventApiHook";
import { useAppViewPicksModalApiResponseSelector } from "src/_common/hooks/selectors/eventSelector";
import { useAppLoader } from "src/_common/hooks/actions/common/appLoaderHook";
// import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
// import { camelCase } from "lodash";
import ShareModal from "src/components/events/modal/shareModal";
import { encryptText } from "src/components/common/Encryptor";

interface ViewPicksModalProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  rodeoEventId?: string;
  performanceId?: string;
  groupId?: string;
  onEditPicks?: () => void;
  showCongrats?: boolean;
}

const ViewPicksModal: React.FC<ViewPicksModalProps> = ({
  isOpen,
  onClose,
  className = "",
  rodeoEventId,
  performanceId,
  groupId,
  onEditPicks,
  showCongrats = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const printableAreaRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  // const previousFocusRef = useRef<HTMLElement | null>(null);

  // API integration
  const eventApi = useEventApi();
  const { callGetRodeoEventYourPicks } = eventApi;
  const MypicksResponse = useAppViewPicksModalApiResponseSelector();
  const noRideRpEventData = useSocketChannel<any>(SOCKET_CHANNEL.NO_RIDE_RP_EVENT);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const loader = useAppLoader();
  // const userDetails = useAppUserDetailsSelector();

  // Fetch your picks when modal opens and we have required params
  useEffect(() => {
    if (!isOpen) return;

    const params = {
      rodeo_event_id: rodeoEventId,
      performance_id: performanceId,
      group_id: groupId,
    };

    callGetRodeoEventYourPicks(
      params,
      (msg: string, resp: any) => {
        console.log("Your picks fetched successfully:", resp);
      },
      (err: string) => {
        console.error("Failed to fetch your picks:", err);
      },
    );
  }, [isOpen]);

  // Refresh when noRideRpEvent fires and matches this modal's rodeo_event_id + performance_id + event_id
  useEffect(() => {
    if (!noRideRpEventData || !isOpen) return;
    const matchesRodeoEvent =
      !noRideRpEventData.rodeo_event_id || String(noRideRpEventData.rodeo_event_id) === String(rodeoEventId);
    const matchesPerformance =
      !noRideRpEventData.performance_id || String(noRideRpEventData.performance_id) === String(performanceId);
    if (!matchesRodeoEvent || !matchesPerformance) return;
    callGetRodeoEventYourPicks(
      { rodeo_event_id: rodeoEventId, performance_id: performanceId, group_id: groupId },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      (_msg: string, _resp: any) => {
        /* no-op */
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      (_err: string) => {
        /* no-op */
      },
    );
  }, [noRideRpEventData, isOpen]);

  const htmlToImageConvert = () => {
    if (!printableAreaRef.current) {
      console.error("Printable area ref is not available");
      return;
    }

    loader.showLoader();

    // Add a small delay to ensure DOM is fully rendered
    setTimeout(() => {
      if (!printableAreaRef.current) return;

      toPng(printableAreaRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#E0B975",
        skipFonts: true, // Skip external CDN fonts to avoid CORS issues
        fontEmbedCSS: `
          @font-face { font-family: "Roboto"; src: url("/fonts/Roboto-Regular.ttf") format("truetype"); font-weight: 400; font-style: normal; }
          @font-face { font-family: "Roboto"; src: url("/fonts/Roboto-Medium.ttf") format("truetype"); font-weight: 500; font-style: normal; }
          @font-face { font-family: "Roboto"; src: url("/fonts/Roboto-Bold.ttf") format("truetype"); font-weight: 700; font-style: normal; }
          @font-face { font-family: "Montserrat"; src: url("/fonts/Montserrat-Regular.ttf") format("truetype"); font-weight: 400; font-style: normal; }
          @font-face { font-family: "Montserrat"; src: url("/fonts/Montserrat-Medium.ttf") format("truetype"); font-weight: 500; font-style: normal; }
          @font-face { font-family: "Montserrat"; src: url("/fonts/Montserrat-SemiBold.ttf") format("truetype"); font-weight: 600; font-style: normal; }
          @font-face { font-family: "Montserrat"; src: url("/fonts/Montserrat-Bold.ttf") format("truetype"); font-weight: 700; font-style: normal; }
          .rider-name { font-size: 10px !important; white-space: nowrap !important; }
          .event-name { font-size: 11px !important; white-space: nowrap !important; }
          .tie-breaker-label, .tie-breaker-code { font-size: 14px !important; white-space: nowrap !important; }
          .tie-breaker-section { white-space: nowrap !important; }
          .view-picks-column-headers .column-header{font-size: 8px !important; white-space: nowrap !important;}
        `,
        style: {
          padding: "10px 10px 20px 10px",
        },
        filter: (node: HTMLElement) => {
          // Filter out elements that might cause issues
          const exclusionClasses = ["btn-close-view-picks", "view-picks-share-btn"];
          return !exclusionClasses.some((classname) => node?.classList?.contains(classname));
        },
      })
        .then((dataUrl: string) => {
          console.log("Image conversion successful, dataUrl length:", dataUrl.length);
          const formData = new FormData();
          formData.append("share_image", dataUrl);

          eventApi.callGetShareImageLink(
            formData,
            (message: string, resp: any) => {
              console.log("Share image API response:", message, resp);
              if (resp?.file_link) {
                console.log(
                  resp.file_link.substring(resp.file_link.lastIndexOf("/") + 1, resp.file_link.lastIndexOf(".")),
                );
                const link =
                  window.location.origin +
                  "/share?" +
                  resp.file_link.substring(resp.file_link.lastIndexOf("/") + 1, resp.file_link.lastIndexOf("."));

                console.log("Share link generated:", link);
                setShareLink(link);
                setShowShareModal(true);
              }
              loader.hideLoader();
            },
            (error: string) => {
              console.error("Failed to get share image link:", error);
              loader.hideLoader();
            },
          );
        })
        .catch((err: any) => {
          console.error("Failed to convert to image. Error details:", err);
          alert("Failed to generate share image. Please try again.");
          loader.hideLoader();
        });
    }, 300);
  };

  const handleEditPicksClick = () => {
    // Close ViewPicks modal and open MakePicks modal
    onClose();
    if (onEditPicks) {
      // Small delay to ensure smooth modal transition
      setTimeout(() => {
        onEditPicks();
      }, 100);
    }
  };

  const handleRodeoStandingsClick = () => {
    onClose();
    const eventId = rodeoEventId || MypicksResponse?.rodeo_event_dtls?.id;
    const perfId = performanceId || MypicksResponse?.performance?.id;
    if (eventId) {
      const path = perfId
        ? `/leaderboard/${encryptText(String(eventId))}/${encryptText(String(perfId))}`
        : `/leaderboard/${encryptText(String(eventId))}`;
      history.push({
        pathname: path,
        state: { tab: "rodeos" },
      });
    }
  };

  const handleShareClick = () => {
    htmlToImageConvert();
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  // Check if all events have winning_declare as 0 (meaning picks can still be edited)
  const canEditPicks = () => {
    if (!MypicksResponse?.team || MypicksResponse.team.length === 0) {
      return false;
    }
    return MypicksResponse.team.every((event: any) => event.winning_declare === 0);
  };

  if (!isOpen) return null;

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      className={`view-picks-modal ${className}`}
      centered
      size="lg"
    >
      <Modal.Body className="p-0">
        <div
          ref={modalRef}
          className="view-picks-modal-content"
          role="dialog"
          aria-modal="true"
          aria-label="View Picks Modal"
          tabIndex={-1}
        >
          {/* Close button */}
          <button className="btn-close-view-picks" onClick={onClose} aria-label="Close modal">
            ×
          </button>
          {/* Congratulations section - only shown first time after completing all picks */}
          {showCongrats && (
            <div id="congratsImgSection">
              <h4 className="rider-name-content text-center pt-2">
                <strong>Congratulations! Your Rodeo Picks are in!!!</strong>
              </h4>
            </div>
          )}
          {/* Header */}
          <div className="print-able-area" ref={printableAreaRef}>
            <div className="view-picks-header">
              <div className="view-picks-brand-info">
                {MypicksResponse?.performance?.avatar?.thumb ? (
                  <div className="view-picks-logo">
                    <img src={MypicksResponse?.performance?.avatar?.thumb} alt="Logo" />
                  </div>
                ) : null}
                <div className="view-picks-event-info">
                  <h2 className="view-picks-title">{MypicksResponse?.group_dtls?.title}</h2>
                  <p className="view-picks-title">{MypicksResponse?.rodeo_event_dtls?.title}</p>
                  <p className="view-picks-location">
                    {MypicksResponse?.rodeo_event_dtls?.location} - {MypicksResponse?.performance?.title}
                  </p>
                </div>
              </div>
              <button className="view-picks-share-btn" onClick={handleShareClick}>
                Share <i className="fas fa-share"></i>
              </button>
            </div>
            {/* Tie Breaker Code */}
            {MypicksResponse?.user_performance_tie_breaker &&
            MypicksResponse?.user_performance_tie_breaker?.tie_breaker_code !== 99999 ? (
              <div className="tie-breaker-section">
                <span className="tie-breaker-label">Tie Breaker Code: </span>
                <span className="tie-breaker-code">
                  {MypicksResponse?.user_performance_tie_breaker?.tie_breaker_code}
                </span>
              </div>
            ) : null}
            {/* Events list */}
            <div className="view-picks-events-list">
              {/* Column headers */}
              <div className="view-picks-column-headers">
                <div className="column-header-container w-50">
                  <span className="column-header">DISCIPLINES / WINNERS</span>
                </div>
                <div className="column-header-container">
                  <span className="column-header">MY PICKS</span>
                </div>
              </div>
              {MypicksResponse?.team && MypicksResponse.team.length > 0 ? (
                MypicksResponse.team.map((event: any, index: number) => {
                  const isWrongPick = event.winning_declare == 1 && event.is_winner == 0;
                  const winners =
                    Array.isArray(event.winners) && event.winners.length > 0
                      ? event.winners.map((w: any) => w.name).join(", ")
                      : null;
                  return (
                    <div
                      key={index}
                      className={
                        event.contestant_details ? "view-picks-event-item" : "view-picks-event-item not-picked-opacity"
                      }
                    >
                      <div className="event-content">
                        <span className={event.contestant_details ? "event-name" : "event-name not-picked"}>
                          {event.no_one_rides === 1 ? (
                            <img src="/img/icon-5.png" />
                          ) : !event.rider_id && event.winning_declare == 0 ? ( // Pick is open but you have not selected.
                            <img src="/img/view-picks-dot-circle.svg" />
                          ) : event.rider_id && event.winning_declare == 0 ? ( // You’ve made a pick, but winner hasn’t been declared yet.
                            <img src="/img/view-picks-icon-check.svg" />
                          ) : event.winning_declare == 1 && event.is_winner ? (
                            <img src="/img/icon-4.png" />
                          ) : event.winning_declare == 1 && event.is_winner == 0 ? (
                            <img src="/img/icon-5.png" />
                          ) : event.contestant_details === null ? (
                            <img src="/img/view-picks-dot-circle.svg" />
                          ) : null}
                          <span className="event-name-text">
                            {event?.event_name}
                            {event.no_one_rides === 1 ? (
                              <span className="event-winners">No Winner</span>
                            ) : (
                              winners && <span className="event-winners">{winners}</span>
                            )}
                          </span>
                        </span>
                        {event.contestant_details ? (
                          <div className="rider-info">
                            <img
                              src={
                                event.contestant_details.rider.id === 0
                                  ? event.contestant_details.contestant_dtls?.avatar?.thumb
                                  : event.contestant_details.rider.thumb
                              }
                              alt={event.name}
                              className="rider-avatar"
                            />
                            <span
                              className={`${isWrongPick || event.no_one_rides === 1 ? "rider-name rider-name--strikeout" : "rider-name"}${event.winning_declare == 1 && event.is_winner ? " rider-name--winner" : ""}`}
                            >
                              {event.name}
                            </span>
                          </div>
                        ) : (
                          <div className="rider-info">
                            <img src="/img/icon-7.png" className="rider-avatar" />
                            <span className="not-picked">Not Picked</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-events-message">
                  <p>No events available</p>
                </div>
              )}
            </div>
          </div>
          {/* Action buttons */}
          <div className="view-picks-actions">
            {canEditPicks() && (
              <button className="btn-edit-picks" onClick={handleEditPicksClick}>
                Edit Picks <i className="fas fa-external-link-alt"></i>
              </button>
            )}
            <button className="btn-rodeo-standings" onClick={handleRodeoStandingsClick}>
              Rodeo Standings <i className="fas fa-trophy"></i>
            </button>
          </div>
        </div>
      </Modal.Body>
      <ShareModal
        onClose={handleCloseShareModal}
        shouldShow={showShareModal}
        performanceDetails={MypicksResponse?.performance || { title: MypicksResponse?.group_dtls?.title || "" }}
        url={shareLink}
      />
    </Modal>
  );
};

export default ViewPicksModal;
